<script setup lang="ts">
  import { DefaultTable, IconButton, Tooltip } from '@/shared/ui';
  import type { MemberTrainingStatus, TrainingDetailTrainee } from '@/entities/Training';
  import TrainingStatusBadge from '../TrainingStatusBadge.vue';
  import TrainingCollaboratorStatusMenu from '../TrainingCollaboratorStatusMenu.vue';
  import { TRAINING_EMPTY_VALUE } from '../../lib/trainingEmptyValue';
  import UploadActionIcon from '@/shared/assets/icons/UploadActionIcon.svg';
  import CertificateAddIcon from '@/shared/assets/icons/CertificateAddIcon.svg';
  import CertificateIcon from '@/shared/assets/icons/CertificateIcon.svg';

  const props = withDefaults(
    defineProps<{
      trainees: TrainingDetailTrainee[];
      isChangingStatus?: (id: number) => boolean;
      generatingIds?: number[];
    }>(),
    { isChangingStatus: () => false, generatingIds: () => [] }
  );

  const emit = defineEmits<{
    'change-status': [id: number, status: MemberTrainingStatus];
    'upload-certificate': [trainee: TrainingDetailTrainee];
    'view-certificate': [trainee: TrainingDetailTrainee];
    'generate-certificate': [trainee: TrainingDetailTrainee];
  }>();

  function isGenerating(trainee: TrainingDetailTrainee): boolean {
    return props.generatingIds.includes(trainee.id);
  }

  const HEADERS = ['Colaborador', 'Matrícula', 'Cargo', 'Status', 'Ações'];
</script>

<template>
  <DefaultTable data-testid="view-training-trainees-table">
    <thead class="bg-white-01">
      <tr>
        <th
          v-for="header in HEADERS"
          :key="header"
          class="text-left text-xs font-semibold text-gray-07 px-4 py-3 whitespace-nowrap align-middle"
        >
          {{ header }}
        </th>
      </tr>
    </thead>
    <tbody class="border-t border-gray-02">
      <tr
        v-for="trainee in trainees"
        :key="trainee.id"
        data-testid="view-training-trainee-row"
        class="border-b border-gray-02 last:border-b-0"
      >
        <td class="px-4 py-3 text-sm text-gray-07">
          {{ trainee.name }}
        </td>
        <td class="px-4 py-3 text-sm text-gray-07">
          {{ trainee.registrationNumber }}
        </td>
        <td class="px-4 py-3 text-sm text-gray-07">
          {{ trainee.position?.name ?? TRAINING_EMPTY_VALUE }}
        </td>
        <td class="px-4 py-3">
          <TrainingStatusBadge :status="trainee.status" />
        </td>
        <td class="px-4 py-3">
          <div class="flex items-center gap-1 text-gray-xx-02">
            <TrainingCollaboratorStatusMenu
              :status="trainee.status"
              label="Mudar status"
              :loading="isChangingStatus(trainee.id)"
              @change="(status) => emit('change-status', trainee.id, status)"
            />
            <Tooltip
              content="Upload certificado"
              class="flex items-center"
            >
              <IconButton
                :src="UploadActionIcon"
                alt="Upload certificado"
                class="w-6 h-6"
                :disabled="trainee.status !== 'COMPLETED'"
                @click="emit('upload-certificate', trainee)"
              />
            </Tooltip>
            <Tooltip
              content="Gerar certificado"
              class="flex items-center"
            >
              <IconButton
                :src="CertificateAddIcon"
                alt="Gerar certificado"
                class="w-6 h-6"
                :disabled="trainee.status !== 'COMPLETED'"
                :loading="isGenerating(trainee)"
                @click="emit('generate-certificate', trainee)"
              />
            </Tooltip>
            <Tooltip
              content="Visualizar certificado"
              class="flex items-center"
            >
              <IconButton
                :src="CertificateIcon"
                alt="Visualizar certificado"
                class="w-6 h-6"
                :disabled="!trainee.hasCertificate"
                @click="emit('view-certificate', trainee)"
              />
            </Tooltip>
          </div>
        </td>
      </tr>
      <tr v-if="trainees.length === 0">
        <td
          :colspan="HEADERS.length"
          data-testid="view-training-trainees-empty"
          class="px-4 py-6 text-center text-sm text-gray-05"
        >
          Nenhum colaborador vinculado ao treinamento
        </td>
      </tr>
    </tbody>
  </DefaultTable>
</template>
